import { GITLAB_BASE_URL } from "./constants";

const MAX_AVATAR_CACHE_ENTRIES = 150;
const AVATAR_FETCH_TIMEOUT_MS = 10000;
const avatarImageCache = new Map();
const inFlightAvatarRequests = new Map();

const normalizeUrl = (url) => {
	try {
		return new URL(url).toString();
	} catch (_error) {
		return "";
	}
};

export const isGitLabAvatarUrl = (url) => {
	const normalizedUrl = normalizeUrl(url);
	if (!normalizedUrl) return false;

	try {
		return new URL(normalizedUrl).origin === new URL(GITLAB_BASE_URL).origin;
	} catch (_error) {
		return false;
	}
};

const trimAvatarCache = () => {
	while (avatarImageCache.size > MAX_AVATAR_CACHE_ENTRIES) {
		const oldestKey = avatarImageCache.keys().next().value;
		const oldestUrl = avatarImageCache.get(oldestKey);
		if (oldestUrl) URL.revokeObjectURL(oldestUrl);
		avatarImageCache.delete(oldestKey);
	}
};

export const clearAvatarImageCache = () => {
	for (const objectUrl of avatarImageCache.values()) {
		URL.revokeObjectURL(objectUrl);
	}
	avatarImageCache.clear();
	inFlightAvatarRequests.clear();
};

export const fetchAuthenticatedAvatar = (url, accessToken) => {
	const normalizedUrl = normalizeUrl(url);
	if (!normalizedUrl || !accessToken) {
		return Promise.reject(new Error("Missing avatar URL or access token"));
	}

	const cachedUrl = avatarImageCache.get(normalizedUrl);
	if (cachedUrl) return Promise.resolve(cachedUrl);

	const inFlightRequest = inFlightAvatarRequests.get(normalizedUrl);
	if (inFlightRequest) return inFlightRequest;

	const request = (async () => {
		const controller = new AbortController();
		const timeoutId = window.setTimeout(
			() => controller.abort(),
			AVATAR_FETCH_TIMEOUT_MS,
		);

		try {
			const response = await fetch(normalizedUrl, {
				headers: {
					Accept: "image/*",
					Authorization: `Bearer ${accessToken}`,
				},
				signal: controller.signal,
			});

			if (!response.ok) {
				throw new Error(`GitLab avatar request failed: ${response.status}`);
			}

			const blob = await response.blob();
			if (blob.type && !blob.type.startsWith("image/")) {
				throw new Error(`Unexpected avatar content type: ${blob.type}`);
			}

			const objectUrl = URL.createObjectURL(blob);
			avatarImageCache.set(normalizedUrl, objectUrl);
			trimAvatarCache();
			return objectUrl;
		} finally {
			window.clearTimeout(timeoutId);
			inFlightAvatarRequests.delete(normalizedUrl);
		}
	})();

	inFlightAvatarRequests.set(normalizedUrl, request);
	return request;
};
